import { useEffect, useRef } from 'react';
import { formatTime12 } from '../lib/dates.js';
import {
  typeLabel,
  STATUS_UPCOMING,
  STATUS_COMPLETED,
  STATUS_CANCELLED,
} from '../lib/constants.js';

/**
 * Overflow menu built on a native <details>.
 *
 * <details> supplies open/close behaviour and keyboard support with no menu
 * library and no custom event wiring. The only thing added is closing when the
 * user clicks elsewhere or presses Escape.
 */
function ActionsMenu({ children }) {
  const ref = useRef(null);

  useEffect(() => {
    function handlePointerDown(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        ref.current.open = false;
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape' && ref.current?.open) {
        ref.current.open = false;
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  function close() {
    if (ref.current) ref.current.open = false;
  }

  return (
    <details className="actions-menu" ref={ref}>
      <summary aria-label="إجراءات إضافية" title="إجراءات إضافية">
        ⋮
      </summary>
      <div className="actions-menu-popover" role="menu">
        {children(close)}
      </div>
    </details>
  );
}

export default function MeetingCard({ meeting, canEdit, onEdit, onStatus, onDelete }) {
  const isDone = meeting.status === STATUS_COMPLETED;
  const isCancelled = meeting.status === STATUS_CANCELLED;

  return (
    <article className="meeting-card" data-type={meeting.type} data-status={meeting.status}>
      <div className="meeting-time">
        <span className="meeting-time-value" dir="rtl">
          {formatTime12(meeting.time)}
        </span>
      </div>

      <div className="meeting-body">
        <h3 className="meeting-title">{meeting.title}</h3>

        <div className="meeting-facts">
          <span className="badge">{typeLabel(meeting.type)}</span>

          {meeting.status !== STATUS_UPCOMING && (
            <span className="badge badge-status" data-status={meeting.status}>
              {meeting.status}
            </span>
          )}

          {meeting.location && (
            <span className="meeting-fact">
              <span aria-hidden="true">📍</span>
              {meeting.location}
            </span>
          )}
        </div>

        {meeting.notes && <p className="meeting-notes">{meeting.notes}</p>}
      </div>

      {/* Only the two frequent, non-destructive actions are visible. Cancel and
          delete sit inside the overflow menu so a mis-tap cannot destroy a
          record. */}
      {canEdit && (
        <div className="meeting-actions">
          {!isDone && !isCancelled && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => onStatus(meeting, STATUS_COMPLETED)}
              title="تعليم الموعد كمنجز"
            >
              ✔ إنجاز
            </button>
          )}

          <button
            type="button"
            className="btn btn-sm"
            onClick={() => onEdit(meeting)}
            title="تعديل الموعد"
          >
            ✎ تعديل
          </button>

          <ActionsMenu>
            {(close) => (
              <>
                {(isDone || isCancelled) && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      onStatus(meeting, STATUS_UPCOMING);
                    }}
                  >
                    ↺ إعادة إلى قادم
                  </button>
                )}

                {!isCancelled && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      onStatus(meeting, STATUS_CANCELLED);
                    }}
                  >
                    ✕ إلغاء
                  </button>
                )}

                <button
                  type="button"
                  role="menuitem"
                  className="is-danger"
                  onClick={() => {
                    close();
                    onDelete(meeting);
                  }}
                >
                  🗑 حذف
                </button>
              </>
            )}
          </ActionsMenu>
        </div>
      )}
    </article>
  );
}
